import { produce } from 'immer';
import { memoize } from 'lodash-es';
import { formatAnalyticsValue } from 'services/analytics/analytics.helpers';
import { DepositMode } from 'services/recharges/recharges.config';
import { TopupMethod } from 'services/recharges/recharges.types';
import colors from 'style-utils/colors';
import { Theme } from 'theme';
import { calcGatewayCircuitComposeId } from 'utils/functions/payments';
import { RechargeFormValues } from './Recharge.types';

export const mapRechargeModeToAnalyticsPaymentMethod = (
  rechargeMode: TopupMethod,
) => {
  const gatewayCircuitComposedId = calcGatewayCircuitComposeId(
    rechargeMode.gatewayId,
    rechargeMode.circuitId,
  );

  switch (gatewayCircuitComposedId) {
    case DepositMode.pdV:
      return 'pdv';
    case DepositMode.bankTransfer:
      return 'bonifico';
    default:
      return formatAnalyticsValue(rechargeMode.description || 'unknown');
  }
};

export const extractAnalyticsDataFromRechargeFormValues = (
  formValues: RechargeFormValues,
): Record<string, string> => {
  const { amount, 'bonus-code': bonusCode } = formValues;

  const analyticsPayload: Record<string, string> = {};

  if (typeof amount === 'number') {
    analyticsPayload['payment_amount'] = (
      amount === -1 ? 0 : amount
    ).toString();
  }

  if (bonusCode) {
    analyticsPayload['promo_code'] = bonusCode;
  }

  return analyticsPayload;
};

export function requestPDF(isApp: boolean) {
  const pdfUrl =
    'https://www.sisal.it/content/dam/new-dam/italy/canali/sisal-it/doc-pdf/doc-vari/vari/dati-bonifico-estero.pdf';

  return isApp
    ? (window.location.href = `${pdfUrl}?open=${pdfUrl}`)
    : window.open(pdfUrl, '_blank');
}

export const rechargeFormAlertBarTheme: (globalTheme: Theme) => Theme = memoize(
  (globalTheme) =>
    produce(globalTheme, (draft) => {
      draft.alertBar.root.background = colors.messageBackground;
      draft.alertBar.root.color = colors.greenUltraDark;
      draft.alertBar.root.borderBottom = `2px solid ${colors.gameBonusValidityLabel}`;

      draft.alertBar.root.mobile.padding = '16px 1px';
      draft.alertBar.root.tablet.padding = '16px 1px';
      draft.alertBar.root.desktop.padding = '11px 20px';

      draft.alertBar.alertColumn.xs.padding = '0 15px';
      draft.alertBar.alertColumn.mobile.padding = '0 15px';
      draft.alertBar.alertColumn.tablet.padding = '0 15px';

      draft.alertBar.alertColumn.xs.minHeight = '';
      draft.alertBar.alertColumn.mobile.minHeight = '';
      draft.alertBar.alertColumn.tablet.minHeight = '';
      draft.alertBar.alertColumn.desktop.minHeight = '';
    }),
);

export const rechargeCustomAlertBarTheme: (globalTheme: Theme) => Theme =
  memoize((globalTheme) =>
    produce(globalTheme, (draft) => {
      draft.customAlertBarTheme.alignItems = 'flex-start';
      draft.customAlertBarTheme.padding = '8px';
      draft.customAlertBarTheme.gap = '4px';
      draft.customAlertBarTheme.mobile.borderRadius = '5px';
      draft.customAlertBarTheme.tablet.borderRadius = '5px';
      draft.customAlertBarTheme.desktop.borderRadius = '5px';
    }),
  );

export const RechargeSubmitFeedbackTheme: (globalTheme: Theme) => Theme =
  memoize((globalTheme) =>
    produce(globalTheme, (draft) => {
      draft.customAlertBarTheme.mobile.borderRadius = '6px';
      draft.customAlertBarTheme.tablet.borderRadius = '6px';
      draft.customAlertBarTheme.desktop.borderRadius = '6px';
    }),
  );
